import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Mail, MapPin, Building, DollarSign, Users, 
  Clock, Star, CheckCircle, Bookmark, Trash2,
  Sparkles, ArrowLeft, Filter, Search, SortAsc, SortDesc,
  Heart, Edit2, ChevronDown, Share2, RefreshCw,
  Bell, Loader2, X
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getCampEmails, updateCampStatus } from '@/services/campEmailService';
import { useAuth } from '@/contexts/AuthContext';
import CampDetailsModal from '@/components/camps/CampDetailsModal';
import Icon from '@/components/ui/Icon';
import { Button } from '@/components/ui/button';
import { useThemeSettings } from '@/contexts/ThemeContext';
import { useToast } from '@/components/ui/use-toast';
import risingSquadLogo from '@/assets/rising-squad.png';
import UnifiedProgramCard from '@/components/cards/UnifiedProgramCard';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import useEmailConnection from '@/hooks/email/useEmailConnection';
import emailIntelligenceService from '@/services/emailIntelligenceService';
import actionItemsService from '@/services/actionItemsService';
import campStatusService from '@/services/campStatusService';
import AppLoadingSpinner from '@/components/loading/AppLoadingSpinner';
import webhookService from '@/services/n8n/webhookService';
import GmailOAuth from '@/components/auth/GmailOAuth';
import { deduplicateCamps } from '@/utils/campDeduplication';
import { deduplicateEmailScoutWithMyPrograms } from '@/services/campDeduplicationService';
import { UserProgram } from '@/api/entities';

const isCampVisible = (camp) => {
  const status = camp?.user_status;
  return !status || status === 'pending' || status === 'new';
};

const computeStats = (campList = []) => {
  const pendingCount = campList.filter((camp) => isCampVisible(camp)).length;
  return {
    total: campList.length,
    pending: pendingCount,
    favorite: campList.filter((camp) => camp.user_status === 'favorited').length,
    hidden: campList.filter((camp) => camp.user_status === 'hidden').length
  };
};

// Helper to parse age range from discovered camp data
const parseAgeRange = (ageStr) => {
  if (!ageStr) return { age_range: null, age_min: null, age_max: null };
  
  // Handle formats like "8-12", "8 - 12", "8", "Ages 8-12"
  const cleaned = ageStr.toString().replace(/ages?\s*/i, '').trim();
  const parts = cleaned.split(/[-–—]/).map(p => p.trim());
  
  if (parts.length === 2) {
    const min = parseInt(parts[0]);
    const max = parseInt(parts[1]);
    if (!isNaN(min) && !isNaN(max)) {
      return {
        age_range: `Ages ${min}-${max}`,
        age_min: min,
        age_max: max
      };
    }
  } else if (parts.length === 1) {
    const age = parseInt(parts[0]);
    if (!isNaN(age)) {
      return {
        age_range: `Ages ${age}`,
        age_min: age,
        age_max: age
      };
    }
  }
  
  return {
    age_range: ageStr.toString().startsWith('Ages') ? ageStr : `Ages ${ageStr}`,
    age_min: null,
    age_max: null
  };
};

// Transform discovered camp data to UnifiedProgramCard format
const transformCampToProgram = (camp) => {
  const ageData = parseAgeRange(camp.child_age);
  
  return {
    id: camp.id || camp.camp_id,
    name: camp.camp_name || camp.name,
    title: camp.camp_name || camp.name,
    organization: camp.camp_organization,
    location: camp.camp_address || camp.camp_location,
    formatted_address: camp.camp_address || camp.camp_location,
    general_location: camp.camp_address || camp.camp_location,
    address: camp.camp_address || camp.camp_location,
    cost_total: camp.cost_total || camp.camp_cost,
    regularPrice: camp.cost_total || camp.camp_cost,
    price: camp.cost_total || camp.camp_cost ? `$${camp.cost_total || camp.camp_cost}` : null,
    age_range: ageData.age_range,
    age_min: ageData.age_min,
    age_max: ageData.age_max,
    image_url: camp.image_url || camp.photo_url,
    photo_url: camp.image_url || camp.photo_url,
    category: camp.category || 'Camp',
    primary_category: camp.category || 'Camp',
    // Include deduplication metadata (used by click handler)
    _emailCount: camp._emailCount || camp._totalEmails || 1,
    _hasMultipleEmails: (camp._emailCount || camp._totalEmails || 1) > 1,
    _relatedEmailIds: camp._relatedEmailIds || [camp.id],
    _allEmails: camp._allEmails || [camp],
    // Email source info stored in program_data for reference
    program_data: {
      ...camp,
      email_source: camp.camp_email
    }
  };
};

const DiscoveredCampsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { theme } = useThemeSettings();
  const isDarkTheme = theme === 'dark';
  const { toast } = useToast();
  const { isConnected, isLoading: isConnectionLoading } = useEmailConnection();
  
  const [camps, setCamps] = useState([]);
  const [filteredCamps, setFilteredCamps] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    favorite: 0,
    hidden: 0
  });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCamp, setSelectedCamp] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Email intelligence state
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [myPrograms, setMyPrograms] = useState([]);
  
  // Email Scout Intelligence Foundation state
  const [emails, setEmails] = useState([]);
  const [emailsByCamp, setEmailsByCamp] = useState({});
  const [actionItems, setActionItems] = useState([]);
  const [campStatuses, setCampStatuses] = useState([]);
  const [isLoadingIntelligence, setIsLoadingIntelligence] = useState(false);
  const [activeTab, setActiveTab] = useState('camps'); // 'camps', 'emails', 'action-items', 'status'
  const [updatingActionItems, setUpdatingActionItems] = useState(new Set());
  
  // Filter and search state - enhanced with email intelligence filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [urgencyFilter, setUrgencyFilter] = useState('all');
  const [sortBy, setSortBy] = useState('email_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [groupBy, setGroupBy] = useState('none'); // none, category, camp, date, status

  /**
   * Format timestamp as "X hrs ago"
   */
  const formatTimeAgo = useCallback((timestamp) => {
    if (!timestamp) return 'Never';
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHrs = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHrs < 24) return `${diffHrs} hr${diffHrs !== 1 ? 's' : ''} ago`;
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } catch (err) {
      return 'Unknown';
    }
  }, []);

  /**
   * Handle sync now
   */
  const handleSyncNow = useCallback(async () => {
    if (!user?.id || isSyncing) return;

    try {
      setIsSyncing(true);
      await webhookService.processDailyEmails(user.id);
      toast({
        title: 'Email sync started',
        description: 'Your emails are being processed. This may take a few minutes.'
      });
      
      // Refresh data after a delay
      setTimeout(() => {
        loadCamps();
        fetchEmailIntelligence();
        fetchIntelligenceData();
      }, 5000);
    } catch (err) {
      console.error('Error syncing emails:', err);
      toast({
        title: 'Sync failed',
        description: err.message || 'Failed to start email sync',
        variant: 'destructive'
      });
    } finally {
      setIsSyncing(false);
    }
  }, [user?.id, isSyncing]);

  /**
   * Fetch email intelligence summary
   */
  const fetchEmailIntelligence = useCallback(async () => {
    if (!user?.id) return;

    try {
      const summary = await emailIntelligenceService.getDashboardSummary(user.id);
      setDashboardSummary(summary);
    } catch (err) {
      console.error('Error fetching email intelligence summary:', err);
    }
  }, [user?.id]);

  /**
   * Fetch Email Scout Intelligence Foundation data
   */
  const fetchIntelligenceData = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoadingIntelligence(true);
      
      // Fetch all intelligence data in parallel
      const [emailsData, emailsByCampData, actionItemsData, campStatusesData] = await Promise.all([
        emailIntelligenceService.getEmails(user.id, {}, { page: 1, pageSize: 100 }),
        emailIntelligenceService.getEmailsBycamp(user.id),
        actionItemsService.getActionItems(user.id),
        Promise.all([
          campStatusService.getCampsByStatus(user.id, 'action_needed'),
          campStatusService.getCampsByStatus(user.id, 'considering'),
          campStatusService.getCampsByStatus(user.id, 'confirmed'),
        ]).then(results => results.flat())
      ]);

      setEmails(emailsData?.emails || []);
      setEmailsByCamp(emailsByCampData || {});
      setActionItems(actionItemsData || []);
      setCampStatuses(campStatusesData || []);
    } catch (err) {
      console.error('Error fetching intelligence data:', err);
    } finally {
      setIsLoadingIntelligence(false);
    }
  }, [user?.id]);

  /**
   * Load camps data
   */
  const loadCamps = useCallback(async () => {
    if (!user?.id) return;

    try {
      setIsLoading(true);
      
      // Load email scout camps and user programs in parallel
      const [campsData, programsData] = await Promise.all([
        getCampEmails(user.id, { 
          page: 1, 
          pageSize: 100, // Load more camps for the dedicated page
          sortBy: sortBy,
          sortOrder: sortOrder
        }),
        (async () => {
          try {
            const userData = await UserProgram.list();
            return userData || [];
          } catch (err) {
            console.warn('Error loading user programs for deduplication:', err);
            return [];
          }
        })()
      ]);
      
      // Filter out records where camp_name is null or empty
      const validCamps = (campsData || []).filter((camp) => 
        camp.camp_name && camp.camp_name.trim() !== ''
      );

      // First deduplicate email scout camps by camp name (internal deduplication)
      const deduplicatedEmailScoutCamps = deduplicateCamps(validCamps, { keepAllData: true });

      // Then deduplicate with My Programs camps
      const fullyDeduplicated = deduplicateEmailScoutWithMyPrograms(
        deduplicatedEmailScoutCamps,
        programsData
      );

      // Filter to only show email scout camps (exclude pure My Programs camps)
      const emailScoutOnly = fullyDeduplicated.filter(camp => 
        camp._source === 'email-scout' || camp._source === 'both'
      );

      setMyPrograms(programsData);
      setCamps(emailScoutOnly);
      setFilteredCamps(emailScoutOnly);
      setStats(computeStats(emailScoutOnly));
    } catch (error) {
      console.error("Error loading camps:", error);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, sortBy, sortOrder]);

  // Load camps data and email intelligence
  useEffect(() => {
    if (user?.id) {
      loadCamps();
      fetchEmailIntelligence();
      fetchIntelligenceData();
    }
  }, [user?.id, sortBy, sortOrder, loadCamps, fetchEmailIntelligence, fetchIntelligenceData]); // Re-fetch when sort changes

  // Calculate unique camps count (after deduplication, this should match camps.length)
  const uniqueCampsCount = useMemo(() => {
    return camps.length;
  }, [camps]);

  // Get sync status
  const syncStatus = useMemo(() => {
    if (!dashboardSummary?.lastSyncStatus) {
      const hasEmails = dashboardSummary?.totalEmails > 0;
      if (hasEmails) {
        return { color: 'green', label: 'Synced' };
      }
      return { color: 'gray', label: 'No sync yet' };
    }
    const status = dashboardSummary.lastSyncStatus.toLowerCase();
    if (status === 'success' || status === 'partial') {
      return { color: 'green', label: 'Healthy' };
    } else if (status === 'failed') {
      return { color: 'red', label: 'Error' };
    }
    return { color: 'gray', label: 'Unknown' };
  }, [dashboardSummary]);

  // Get active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (statusFilter !== 'all') count++;
    if (categoryFilter !== 'all') count++;
    if (urgencyFilter !== 'all') count++;
    return count;
  }, [searchTerm, statusFilter, categoryFilter, urgencyFilter]);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setUrgencyFilter('all');
  }, []);

  // Apply filters and search
  useEffect(() => {
    let filtered = [...camps];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(camp =>
        camp.camp_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        camp.camp_organization?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        camp.camp_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        camp.camp_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(camp => camp.user_status === statusFilter);
    }

    // Apply category filter (if camp has email_category from email intelligence)
    if (categoryFilter !== 'all' && filtered.length > 0) {
      // This would need email data merged with camp data
      // For now, we'll filter if available in the camp object
      filtered = filtered.filter(camp => {
        // Only include camps that have a matching email_category
        // Exclude camps without an email_category when filtering by a specific category
        return camp.email_category === categoryFilter;
      });
    }

    // Apply urgency filter
    if (urgencyFilter !== 'all' && filtered.length > 0) {
      filtered = filtered.filter(camp => {
        // Check if camp has urgency property directly
        if (camp.urgency_level === urgencyFilter) {
          return true;
        }

        // Match camp to emails using camp name or identifier
        const campName = camp.camp_name;
        if (!campName || !emailsByCamp || Object.keys(emailsByCamp).length === 0) {
          // If no camp name or no emails data, cannot determine urgency - exclude camp from results
          return false;
        }

        // Find matching camp entry in emailsByCamp
        // Try matching by camp name (exact match or case-insensitive)
        const campEntry = Object.values(emailsByCamp).find(
          entry => entry.camp?.name === campName || 
                   entry.camp?.name?.toLowerCase() === campName?.toLowerCase()
        );

        // If found, check if any email has matching urgency level
        if (campEntry && campEntry.emails && Array.isArray(campEntry.emails)) {
          return campEntry.emails.some(email => email.urgency_level === urgencyFilter);
        }

        // If no match found, exclude camp from results (urgency filter requires matching emails)
        return false;
      });
    }

    setFilteredCamps(filtered);
  }, [camps, searchTerm, statusFilter, categoryFilter, urgencyFilter, emailsByCamp]);

  // Group camps based on groupBy selection
  const groupedCamps = useMemo(() => {
    if (groupBy === 'none') {
      return null; // No grouping, use filteredCamps directly
    }

    if (groupBy === 'category') {
      // Group by email_category or category field
      const groups = {};
      filteredCamps.forEach((camp) => {
        const category = camp.email_category || camp.category || 'Uncategorized';
        if (!groups[category]) {
          groups[category] = [];
        }
        groups[category].push(camp);
      });
      return groups;
    }

    if (groupBy === 'camp') {
      // Group by camp name (useful when same camp appears multiple times)
      const groups = {};
      filteredCamps.forEach((camp) => {
        const campName = camp.camp_name || 'Unknown Camp';
        if (!groups[campName]) {
          groups[campName] = [];
        }
        groups[campName].push(camp);
      });
      return groups;
    }

    if (groupBy === 'date') {
      // Group by email date (formatted as date string)
      const groups = {};
      filteredCamps.forEach((camp) => {
        let dateKey = 'No Date';
        if (camp.email_date) {
          try {
            const date = new Date(camp.email_date);
            if (!isNaN(date.getTime())) {
              // Format as "YYYY-MM-DD" for grouping
              dateKey = date.toISOString().split('T')[0];
            }
          } catch (e) {
            // Keep "No Date" if parsing fails
          }
        }
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(camp);
      });
      // Sort date keys in descending order (most recent first)
      const sortedGroups = {};
      Object.keys(groups)
        .sort((a, b) => {
          if (a === 'No Date') return 1;
          if (b === 'No Date') return -1;
          return b.localeCompare(a); // Descending order
        })
        .forEach((key) => {
          sortedGroups[key] = groups[key];
        });
      return sortedGroups;
    }

    return null;
  }, [filteredCamps, groupBy]);

  // Format date for display
  const formatDateGroup = useCallback((dateKey) => {
    if (dateKey === 'No Date') return 'No Date';
    try {
      const date = new Date(dateKey);
      if (isNaN(date.getTime())) return dateKey;
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateKey;
    }
  }, []);

  // Handle camp status updates
  const handleUpdateCampStatus = async (campId, status) => {
    try {
      await updateCampStatus(campId, status);
      
      // Update local state
      let nextVisibleCamps = [];
      setCamps((prev) => {
        const updated = prev.map((camp) =>
          camp.id === campId ? { ...camp, user_status: status } : camp
        );
        const filtered = updated.filter(isCampVisible);
        nextVisibleCamps = filtered;
        return filtered;
      });

      setStats(computeStats(nextVisibleCamps));
    } catch (error) {
      console.error("Error updating camp status:", error);
    }
  };

  const handleFavorite = (campId) => handleUpdateCampStatus(campId, 'favorited');
  const handleMarkReviewed = (campId) => handleUpdateCampStatus(campId, 'reviewed');
  const handleSaveForLater = (campId) => handleUpdateCampStatus(campId, 'saved_for_later');
  const handleDelete = (campId) => handleUpdateCampStatus(campId, 'hidden');

  const handleViewDetails = (camp) => {
    setSelectedCamp(camp);
    setShowModal(true);
  };

  // Handlers for UnifiedProgramCard - these receive program objects, not camp IDs
  const handleCardDelete = (program) => {
    // Find the original camp by matching the program ID with camp ID
    const originalCamp = camps.find(c => (c.id || c.camp_id) === program.id);
    if (originalCamp?.id) {
      handleDelete(originalCamp.id);
    }
  };

  const handleCardSave = (program) => {
    // Find the original camp by matching the program ID with camp ID
    const originalCamp = camps.find(c => (c.id || c.camp_id) === program.id);
    if (originalCamp?.id) {
      handleSaveForLater(originalCamp.id);
    }
  };

  const handleCardClick = (program) => {
    // Find the original camp by matching the program ID with camp ID
    const originalCamp = camps.find(c => (c.id || c.camp_id) === program.id);
    if (originalCamp) {
      // Navigate to ProgramDetailPage with email-scout mode
      const campId = originalCamp.id || originalCamp.camp_id;
      if (campId) {
        navigate(`/program/${campId}?mode=email-scout&source=email-scout`);
      } else {
        console.warn('Cannot navigate: missing camp ID', originalCamp);
        toast({
          title: 'Navigation error',
          description: 'Unable to open program details. Missing camp ID.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleCardEdit = (program) => {
    // Find the original camp by matching the program ID with camp ID
    const originalCamp = camps.find(c => (c.id || c.camp_id) === program.id);
    if (originalCamp) {
      // Navigate to ProgramDetailPage with email-scout mode and edit enabled
      const campId = originalCamp.id || originalCamp.camp_id;
      if (campId) {
        navigate(`/program/${campId}?mode=email-scout&source=email-scout&edit=true`);
      } else {
        console.warn('Cannot navigate: missing camp ID', originalCamp);
        toast({
          title: 'Navigation error',
          description: 'Unable to open program details. Missing camp ID.',
          variant: 'destructive'
        });
      }
    }
  };

  /**
   * Handle marking action item as completed
   */
  const handleMarkActionItemComplete = useCallback(async (itemId) => {
    if (updatingActionItems.has(itemId)) return;

    try {
      setUpdatingActionItems(prev => new Set(prev).add(itemId));
      await actionItemsService.updateActionItem(itemId, { status: 'completed' });
      toast({
        title: 'Action item completed',
        description: 'The action item has been marked as completed.',
      });
      // Refresh action items
      await fetchIntelligenceData();
    } catch (err) {
      console.error('Error completing action item:', err);
      toast({
        title: 'Error',
        description: 'Failed to mark action item as completed. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUpdatingActionItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [updatingActionItems, fetchIntelligenceData, toast]);

  /**
   * Handle dismissing action item
   */
  const handleDismissActionItem = useCallback(async (itemId) => {
    if (updatingActionItems.has(itemId)) return;

    try {
      setUpdatingActionItems(prev => new Set(prev).add(itemId));
      await actionItemsService.dismissActionItem(itemId);
      toast({
        title: 'Action item dismissed',
        description: 'The action item has been dismissed.',
      });
      // Refresh action items
      await fetchIntelligenceData();
    } catch (err) {
      console.error('Error dismissing action item:', err);
      toast({
        title: 'Error',
        description: 'Failed to dismiss action item. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setUpdatingActionItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  }, [updatingActionItems, fetchIntelligenceData, toast]);

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Email Scout - Discovered Camps',
        text: `Check out my discovered camps on Summer Squad!`,
        url: window.location.href,
      }).catch(() => {});
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast({
        title: 'Link copied',
        description: 'Page link copied to clipboard.',
      });
    }
  };

  // Show connection prompt if not connected
  if (!isConnectionLoading && !isConnected && camps.length === 0 && !isLoading) {
    return (
      <div className={`min-h-screen ${isDarkTheme ? 'bg-gray-900' : 'bg-gray-100'}`}>
        <div className={`${isDarkTheme ? 'bg-slate-900' : 'bg-white'} border-b ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'}`}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-start gap-3">
              <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${isDarkTheme ? 'bg-slate-800' : 'bg-gradient-to-br from-blue-100 via-blue-50 to-purple-100'} text-brand-600 shadow-inner`}>
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h1 className={`text-2xl font-semibold sm:text-3xl ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>Email Scout</h1>
                <p className={`text-sm ${isDarkTheme ? 'text-slate-400' : 'text-slate-600'}`}>
                  Manage all camps discovered from your emails
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Card>
            <CardContent className="text-center py-8 space-y-4">
              <Mail className="h-16 w-16 text-gray-400 mx-auto" />
              <h2 className="text-xl font-semibold">No Camp Emails Found</h2>
              <p className="text-gray-600">
                Connect your Gmail to automatically discover camp emails
              </p>
              <GmailOAuth
                onSuccess={() => {
                  setTimeout(() => {
                    loadCamps();
                    fetchEmailIntelligence();
                  }, 2000);
                }}
                onError={(err) => toast({ title: 'Connection failed', description: err.message, variant: 'destructive' })}
                buttonText="Connect Gmail"
                className="flex justify-center"
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isLoading || isConnectionLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <AppLoadingSpinner message="Loading discovered camps..." />
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkTheme ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Top Nav - sticky header like ProgramDetailPage */}
      <div className={`bg-gradient-to-r ${isDarkTheme ? 'from-slate-900 to-slate-800' : 'from-purple-50 to-blue-50'} border-b ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'} sticky top-0 z-40`}>
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className={`p-2 ${isDarkTheme ? 'hover:bg-slate-800' : 'hover:bg-gray-100'} rounded-lg transition-colors`}
              aria-label="Go back"
            >
              <ArrowLeft className={`w-5 h-5 ${isDarkTheme ? 'text-slate-200' : 'text-gray-700'}`} />
            </button>
            {/* Logo and Brand Name */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
                <img
                  src={risingSquadLogo}
                  alt="Summer Squad logo"
                  className="w-full h-full object-contain"
                  decoding="async"
                  loading="eager"
                  width={40}
                  height={40}
                />
              </div>
              <span className={`text-2xl font-bold brand-font ${isDarkTheme ? 'text-slate-100' : 'text-gray-900'}`}>Summer Squad</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleShare}
              className={`p-2 ${isDarkTheme ? 'hover:bg-slate-800' : 'hover:bg-gray-100'} rounded-lg transition-colors ${isDarkTheme ? 'text-slate-200 hover:text-slate-100' : 'text-gray-700 hover:text-gray-900'}`}
              aria-label="Share"
              title="Share"
            >
              <Share2 className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Page Header */}
      <div className={`${isDarkTheme ? 'bg-slate-900' : 'bg-white'} border-b ${isDarkTheme ? 'border-slate-700' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start">
            <div className="flex items-start gap-3">
              <div className={`inline-flex h-12 w-12 items-center justify-center rounded-xl ${isDarkTheme ? 'bg-slate-800' : 'bg-gradient-to-br from-blue-100 via-blue-50 to-purple-100'} text-brand-600 shadow-inner`}>
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <h1 className={`text-2xl font-semibold sm:text-3xl ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>Email Scout</h1>
                <p className={`text-sm ${isDarkTheme ? 'text-slate-400' : 'text-slate-600'}`}>
                  Manage all {uniqueCampsCount} camps discovered from your emails
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="pb-12 pt-6">
        <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
          {/* Summary Stats Card */}
          <Card className="shadow-lg border border-gray-200">
            <CardContent className="p-4 sm:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <div>
                  <div className="text-2xl font-bold">{uniqueCampsCount}</div>
                  <div className="text-sm text-gray-600">Total Camps Found</div>
                </div>
                <div>
                  <div className="text-2xl font-bold">{dashboardSummary?.totalEmails || 0}</div>
                  <div className="text-sm text-gray-600">Emails Scanned</div>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <div className={`h-2 w-2 rounded-full ${
                      syncStatus.color === 'green' ? 'bg-green-500' :
                      syncStatus.color === 'red' ? 'bg-red-500' : 'bg-gray-400'
                    }`} />
                    <span className="text-sm font-medium">{syncStatus.label}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Last sync: {formatTimeAgo(dashboardSummary?.lastSyncTime)}
                  </div>
                </div>
              </div>
              
              {isConnected && (
                <Button
                  onClick={handleSyncNow}
                  disabled={isSyncing}
                  variant="outline"
                  size="sm"
                  className="w-full sm:w-auto"
                >
                  {isSyncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Sync Now
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Tab Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <nav aria-label="Email Scout sections" className="border-b border-gray-200 -mx-4 px-4 sm:mx-0 sm:px-0 mb-6">
              <TabsList className="flex gap-6 sm:gap-8 overflow-x-auto hide-scrollbar h-auto bg-transparent p-0 border-0">
                <TabsTrigger
                  value="camps"
                  className="pb-4 border-b-2 font-medium whitespace-nowrap transition-colors flex items-center gap-2 data-[state=active]:border-brand-600 data-[state=active]:text-brand-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:bg-transparent rounded-none"
                >
                  <Bookmark className="w-4 h-4" />
                  <span>Camps ({filteredCamps.length})</span>
                </TabsTrigger>
                <TabsTrigger
                  value="emails"
                  className="pb-4 border-b-2 font-medium whitespace-nowrap transition-colors flex items-center gap-2 data-[state=active]:border-brand-600 data-[state=active]:text-brand-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:bg-transparent rounded-none"
                >
                  <Mail className="w-4 h-4" />
                  <span>Emails ({emails.length})</span>
                </TabsTrigger>
                <TabsTrigger
                  value="action-items"
                  className="pb-4 border-b-2 font-medium whitespace-nowrap transition-colors flex items-center gap-2 data-[state=active]:border-brand-600 data-[state=active]:text-brand-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=inactive]:border-transparent data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:text-gray-900 data-[state=inactive]:bg-transparent rounded-none"
                >
                  <Bell className="w-4 h-4" />
                  <span>Action Items ({actionItems.filter(item => item.status === 'pending' || item.status === 'overdue').length})</span>
                </TabsTrigger>
              </TabsList>
            </nav>

          {/* Enhanced Filter Bar */}
          <Card className="shadow-lg border border-gray-200">
            <CardContent className="p-3 sm:p-4">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by camp name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20"
                    />
                  </div>
                </div>
                
                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                >
                  <SelectTrigger className="w-full md:w-[180px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="discovery">Discovery</SelectItem>
                    <SelectItem value="registration">Registration</SelectItem>
                    <SelectItem value="precamp">Pre-Camp</SelectItem>
                    <SelectItem value="reminder">Reminders</SelectItem>
                    <SelectItem value="logistics">Logistics</SelectItem>
                    <SelectItem value="update">Updates</SelectItem>
                    <SelectItem value="during_camp">During Camp</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={statusFilter}
                  onValueChange={setStatusFilter}
                >
                  <SelectTrigger className="w-full md:w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="favorited">Favorited</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="saved_for_later">Saved for Later</SelectItem>
                  </SelectContent>
                </Select>

                <Select
                  value={urgencyFilter}
                  onValueChange={setUrgencyFilter}
                >
                  <SelectTrigger className="w-full md:w-[180px]">
                    <SelectValue placeholder="Urgency" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Urgency</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="attention">Attention Required</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                  </SelectContent>
                </Select>

                {activeFilterCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearFilters}
                    className="w-full sm:w-auto"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Clear ({activeFilterCount})
                  </Button>
                )}
                </div>

              {/* Grouping and Sorting */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Group by:</span>
                  <Select value={groupBy} onValueChange={setGroupBy}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="category">Category</SelectItem>
                      <SelectItem value="camp">Camp</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="status">Status</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Sort by:</span>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email_date">Recent</SelectItem>
                      <SelectItem value="camp_name">Camp Name</SelectItem>
                      <SelectItem value="camp_organization">Organization</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  >
                    {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              </div>
            </CardContent>
          </Card>

          {/*
          <section>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="rounded-2xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur transition-shadow duration-200 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-gradient-to-br from-blue-100 via-blue-50 to-purple-100 p-2.5 text-blue-600">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Total Discovered</p>
                    <p className="text-2xl font-semibold text-slate-900">{stats.total}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur transition-shadow duration-200 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-gradient-to-br from-yellow-100 via-yellow-50 to-amber-100 p-2.5 text-yellow-600">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Pending Review</p>
                    <p className="text-2xl font-semibold text-slate-900">{stats.pending}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur transition-shadow duration-200 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-gradient-to-br from-purple-100 via-purple-50 to-brand-50 p-2.5 text-brand-600">
                    <Star className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Favorited</p>
                    <p className="text-2xl font-semibold text-slate-900">{stats.favorite}</p>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-white/60 bg-white/80 p-4 shadow-sm backdrop-blur transition-shadow duration-200 hover:shadow-md">
                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-gradient-to-br from-emerald-100 via-emerald-50 to-teal-100 p-2.5 text-emerald-600">
                    <CheckCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Reviewed</p>
                    <p className="text-2xl font-semibold text-slate-900">{stats.hidden}</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <div className="rounded-2xl border border-white/60 bg-white/80 px-4 py-5 shadow-sm backdrop-blur">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div className="relative min-w-[220px] flex-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search camps..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full rounded-full border border-slate-200 bg-white/80 py-2.5 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 shadow-inner focus:border-brand-500/60 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                  />
                </div>

                <div className="relative min-w-[200px] flex-1 lg:max-w-xs">
                  <Filter className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full appearance-none rounded-full border border-slate-200 bg-white/80 py-2.5 pl-11 pr-10 text-sm text-slate-700 shadow-inner focus:border-brand-500/60 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                  >
                    <option value="all">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="favorited">Favorited</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="saved_for_later">Saved for Later</option>
                    <option value="hidden">Hidden</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>

                <div className="relative min-w-[200px] flex-1 lg:max-w-xs">
                  <SortDesc className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full appearance-none rounded-full border border-slate-200 bg-white/80 py-2.5 pl-11 pr-10 text-sm text-slate-700 shadow-inner focus:border-brand-500/60 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                  >
                    <option value="email_date">Sort by Date</option>
                    <option value="camp_name">Sort by Name</option>
                    <option value="camp_organization">Sort by Organization</option>
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="rounded-full border-slate-200 bg-white/80 px-4 py-2 text-sm font-medium text-slate-600 shadow-inner transition-colors hover:border-brand-500/60 hover:text-brand-600"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? (
                    <span className="flex items-center gap-2">
                      <SortAsc className="h-4 w-4" />
                      Asc
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <SortDesc className="h-4 w-4" />
                      Desc
                    </span>
                  )}
                </Button>
              </div>
            </div>
          </section>
          */}

          {/* Tab Content */}
          <TabsContent value="camps" className="mt-6 data-[state=inactive]:hidden" id="camps-panel" role="tabpanel" aria-labelledby="camps-tab">
            <section className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            </div>

            {filteredCamps.length === 0 ? (
              <div className={`rounded-2xl border border-dashed ${isDarkTheme ? 'border-purple-800/70 bg-slate-800' : 'border-purple-200/70 bg-white/80'} p-12 text-center shadow-sm backdrop-blur`}>
                <Sparkles className="mx-auto mb-4 h-12 w-12 text-purple-300" />
                <h3 className={`mb-2 text-lg font-semibold ${isDarkTheme ? 'text-slate-100' : 'text-slate-900'}`}>No camps found</h3>
                <p className={`text-sm ${isDarkTheme ? 'text-slate-400' : 'text-slate-600'}`}>
                  {searchTerm || statusFilter !== 'all'
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No camps have been discovered from your emails yet.'}
                </p>
              </div>
            ) : groupBy === 'status' ? (
              // Status-grouped view (from former Camp Status tab)
              <div className="space-y-4">
                {isLoadingIntelligence ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
                  </div>
                ) : campStatuses.length === 0 ? (
                  <Card className="shadow-lg border border-gray-200">
                    <CardContent className="p-12 text-center">
                      <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No camp statuses</h3>
                      <p className="text-sm text-gray-600">No camp status tracking data available.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {['action_needed', 'considering', 'confirmed'].map((status) => {
                      const statusCamps = campStatuses.filter(camp => camp.status === status);
                      if (statusCamps.length === 0) return null;

                      return (
                        <Card key={status} className="shadow-lg border border-gray-200">
                          <CardContent className="p-6">
                            <h3 className="text-lg font-semibold mb-4 capitalize">
                              {status.replace('_', ' ')} ({statusCamps.length})
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {statusCamps.map((campStatus) => (
                                <div
                                  key={campStatus.id}
                                  className="p-4 rounded-lg border border-gray-200"
                                >
                                  <h4 className="font-medium mb-2">{campStatus.camp_name}</h4>
                                  <p className="text-sm text-gray-600 mb-3">{campStatus.camp_organization}</p>
                                  <div className="space-y-1 text-xs text-gray-500">
                                    {campStatus.is_registered && (
                                      <div>Registered: {campStatus.registration_date || 'N/A'}</div>
                                    )}
                                    {campStatus.total_cost && (
                                      <div>Cost: ${campStatus.total_cost}</div>
                                    )}
                                    {campStatus.balance_due !== null && campStatus.balance_due > 0 && (
                                      <div className="text-amber-600">Balance: ${campStatus.balance_due}</div>
                                    )}
                                    <div>{campStatus.total_emails || 0} emails • {campStatus.unread_emails || 0} unread</div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : groupedCamps ? (
              // Grouped views (category, camp, date)
              <div className="space-y-6">
                {Object.entries(groupedCamps).map(([groupKey, groupCamps]) => {
                  if (groupCamps.length === 0) return null;

                  return (
                    <Card key={groupKey} className="shadow-lg border border-gray-200">
                      <CardContent className="p-6">
                        <h3 className="text-lg font-semibold mb-4 capitalize">
                          {groupBy === 'date' 
                            ? formatDateGroup(groupKey)
                            : groupBy === 'category'
                            ? groupKey
                            : groupKey
                          } ({groupCamps.length})
                        </h3>
                        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                          {groupCamps.map((camp) => {
                            const isFavorited = camp.user_status === 'favorited';
                            const programData = transformCampToProgram(camp);
                            
                            return (
                              <div key={camp.id || `camp-${camp.camp_name}`} className="relative">
                                <UnifiedProgramCard
                                  program={programData}
                                  viewMode="saved"
                                  variant="horizontal"
                                  isSaved={isFavorited}
                                  hideCategoryBadge={false}
                                  statusInlineWithTitle={true}
                                  uniformHeight={true}
                                  onClick={handleCardClick}
                                  onView={handleCardClick}
                                  onEdit={handleCardEdit}
                                  onDelete={handleCardDelete}
                                  onSave={handleCardSave}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              // Card grid view (default - no grouping)
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                {filteredCamps.map((camp) => {
                  const isFavorited = camp.user_status === 'favorited';
                  const programData = transformCampToProgram(camp);
                  
                  return (
                    <div key={camp.id || `camp-${camp.camp_name}`} className="relative">
                      <UnifiedProgramCard
                        program={programData}
                        viewMode="saved"
                        variant="horizontal"
                        isSaved={isFavorited}
                        hideCategoryBadge={false}
                        statusInlineWithTitle={true}
                        uniformHeight={true}
                        onClick={handleCardClick}
                        onView={handleCardClick}
                        onEdit={handleCardEdit}
                        onDelete={handleCardDelete}
                        onSave={handleCardSave}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          </TabsContent>

          <TabsContent value="emails" className="mt-6 data-[state=inactive]:hidden" id="emails-panel" role="tabpanel" aria-labelledby="emails-tab">
            <section className="space-y-4">
              {isLoadingIntelligence ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
                </div>
              ) : emails.length === 0 ? (
                <Card className="shadow-lg border border-gray-200">
                  <CardContent className="p-12 text-center">
                    <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No emails found</h3>
                    <p className="text-sm text-gray-600">No emails have been processed yet.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {Object.entries(emailsByCamp).map(([campIdentifier, campData]) => (
                    <Card key={campIdentifier} className="shadow-lg border border-gray-200">
                      <CardContent className="p-6">
                        <div className="mb-4">
                          <h3 className="text-lg font-semibold">{campData.camp?.name || 'Unknown Camp'}</h3>
                          <p className="text-sm text-gray-600">{campData.camp?.organization}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                            <span>{campData.status?.totalEmails || 0} emails</span>
                            <span>{campData.status?.unreadEmails || 0} unread</span>
                            {campData.status?.hasActionItems && (
                              <span className="text-amber-600">Has action items</span>
                            )}
                          </div>
                        </div>
                        <div className="space-y-2">
                          {campData.emails?.slice(0, 5).map((email) => (
                            <div
                              key={email.id}
                              className={`p-3 rounded-lg border ${
                                !email.is_read ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="text-sm font-medium">{email.email_subject || email.subject}</span>
                                    {!email.is_read && (
                                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">New</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-600">
                                    {new Date(email.email_date).toLocaleDateString()} • {email.email_category || 'uncategorized'}
                                  </p>
                                  {email.urgency_level === 'urgent' && (
                                    <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded">Urgent</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                          {campData.emails?.length > 5 && (
                            <p className="text-xs text-gray-500 text-center pt-2">
                              +{campData.emails.length - 5} more emails
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </section>
          </TabsContent>

          <TabsContent value="action-items" className="mt-6 data-[state=inactive]:hidden" id="action-items-panel" role="tabpanel" aria-labelledby="action-items-tab">
            <section className="space-y-4">
              {isLoadingIntelligence ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
                </div>
              ) : actionItems.length === 0 ? (
                <Card className="shadow-lg border border-gray-200">
                  <CardContent className="p-12 text-center">
                    <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No action items</h3>
                    <p className="text-sm text-gray-600">All caught up! No action items at this time.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {['overdue', 'pending', 'completed', 'dismissed'].map((status) => {
                    const items = actionItems.filter(item => item.status === status);
                    if (items.length === 0) return null;

                    return (
                      <Card key={status} className="shadow-lg border border-gray-200">
                        <CardContent className="p-6">
                          <h3 className="text-lg font-semibold mb-4 capitalize">{status} ({items.length})</h3>
                          <ul className="space-y-3">
                            {items.map((item) => {
                              const isUpdating = updatingActionItems.has(item.id);
                              const canInteract = (status === 'pending' || status === 'overdue') && !isUpdating;

                              return (
                                <li
                                  key={item.id}
                                  className="p-3 rounded-lg border border-gray-200 hover:border-brand-300 transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`px-2 py-0.5 text-xs rounded ${
                                          item.priority === 'high' ? 'bg-red-100 text-red-800' :
                                          item.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-gray-100 text-gray-800'
                                        }`}>
                                          {item.priority}
                                        </span>
                                        <span className="text-sm font-medium">{item.title}</span>
                                      </div>
                                      {item.description && (
                                        <p className="text-xs text-gray-600 mb-2">{item.description}</p>
                                      )}
                                      <div className="flex items-center gap-4 text-xs text-gray-500">
                                        {item.due_date && (
                                          <span>Due: {new Date(item.due_date).toLocaleDateString()}</span>
                                        )}
                                        {item.amount && (
                                          <span className="font-medium text-gray-700">${item.amount}</span>
                                        )}
                                        {item.action_type && (
                                          <span className="capitalize">{item.action_type}</span>
                                        )}
                                      </div>
                                    </div>
                                    {canInteract && (
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleMarkActionItemComplete(item.id)}
                                          disabled={isUpdating}
                                          className="h-8 w-8 p-0"
                                          title="Mark as completed"
                                        >
                                          {isUpdating ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                          ) : (
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                          )}
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => handleDismissActionItem(item.id)}
                                          disabled={isUpdating}
                                          className="h-8 w-8 p-0"
                                          title="Dismiss"
                                        >
                                          <X className="h-4 w-4 text-gray-500" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </section>
          </TabsContent>

          </Tabs>
        </div>
      </main>

      {selectedCamp && (
        <CampDetailsModal
          camp={selectedCamp}
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setSelectedCamp(null);
          }}
          onFavorite={handleFavorite}
          onMarkReviewed={handleMarkReviewed}
          onSaveForLater={handleSaveForLater}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default DiscoveredCampsPage;
